<script>
import DashboardLayout from "../layouts/DashboardLayout.vue";
import StateBlock from "../components/StateBlock.vue";
import {api} from "../services/api";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import DOMPurify from "dompurify";
import {marked} from "marked";

export default {
    name: "ChatHistoryView",
    components: {
        DashboardLayout,
        StateBlock,
        DataTable,
        Column,
    },
    data() {
        return {
            sessions: [],
            messages: [],
        };
    },
    methods: {
        renderMessage(content) {
            const text = String(content || "");
            return DOMPurify.sanitize(text.includes("<!--language-program:") ? marked.parse(text) : text);
        },
        async loadMessages(id) {
            this.messages = (await api.get(`/chat-sessions/${id}/messages`)).data;
        },
        isSenderRole(role) {
            const normalizedRole = String(role || "").toLowerCase();
            return normalizedRole === "user" || normalizedRole === "visitor" || normalizedRole === "human";
        },
        roleLabel(role) {
            const normalizedRole = String(role || "").toLowerCase();
            if (this.isSenderRole(normalizedRole)) return "Visitor";
            if (normalizedRole === "assistant") return "AI Agent";
            if (normalizedRole === "system") return "System";
            return role || "Unknown";
        },
        roleAvatarUrl(message) {
            const normalizedRole = String(message?.role || "").toLowerCase();

            if (this.isSenderRole(normalizedRole)) {
                return "/assets/images/human/user.webp";
            }

            if (normalizedRole === "assistant") {
                return "/assets/images/human/female/female-1.png";
            }

            if (normalizedRole === "system") {
                return "/assets/images/human/female/female-1.png";
            }

            return "https://api.dicebear.com/9.x/identicon/svg?seed=unknown";
        },
    },
    async mounted() {
        this.sessions = (await api.get("/chat-sessions")).data;
    },
};
</script>
<template>
    <DashboardLayout title="Chat History" subtitle="Inspect visitor conversations with your AI Agents.">
        <div class="split">
            <section class="panel">
                <h2>Sessions</h2>
                <StateBlock v-if="sessions.length === 0" title="No chat sessions" message="Widget conversations will appear here." />
                <div v-else>
                    <DataTable
                        :value="sessions"
                        class="data-table"
                        paginator
                        :rows="5"
                        sortField="lastMessageAt"
                        :sortOrder="-1"
                        paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} - {last} / {totalRecords}"
                    >
                        <Column field="visitorId" header="Visitor" />
                        <Column field="lastMessageAt" header="Last message" sortable>
                            <template #body="slotProps">
                                {{ new Date(slotProps.data.lastMessageAt).toLocaleString() }}
                            </template>
                        </Column>
                        <Column header="">
                            <template #body="slotProps">
                                <button class="ghost-button" @click="loadMessages(slotProps.data.id)">View</button>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </section>
            <section class="panel">
                <h2>Messages</h2>
                <StateBlock v-if="messages.length === 0" title="No session selected" message="Select a chat session to view messages." />
                <div v-else class="chat-thread">
                    <div v-for="message in messages" :key="message.id" class="chat-message" :class="{sender: isSenderRole(message.role)}">
                        <div class="chat-message-avatar">
                            <img :src="roleAvatarUrl(message)" :alt="roleLabel(message.role)" />
                        </div>
                        <div class="chat-message-data">
                            <div class="chat-message-data-inner">
                                <div class="message-text">
                                    <span class="badge meta-badge">{{ roleLabel(message.role) }}</span>
                                      <div v-html="renderMessage(message.content)"></div>
                                    <div v-if="message.usedSkills?.length" class="message-skills">
                                        <span style="display: block; font-weight: bold; margin-bottom: 0.25rem">Used Skills:</span>
                                        <span v-for="skill in message.usedSkills" :key="skill.id" class="badge skill-badge">{{ skill.name }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </DashboardLayout>
</template>
<style scoped>
.chat-message .chat-message-data-inner .message-text {
    background-color: #f6f6f6;
    border-radius: 1rem;
    color: #333;
}
.meta-badge {
    margin-bottom: 0.3rem;
    background-color: #111;
    color: #fff;
}
.message-skills {
    margin-top: 0.5rem;
}
.chat-message.sender .chat-message-data-inner .message-text {
    background-color: #4f9cf9;
    border-radius: 1rem;
    color: #fff;
}

.data-table {
    margin-bottom: 0.75rem;
}
</style>
